import * as React from 'react';
import { Translations, EditableVariable } from '../state';
import { VariableEditor } from './variable-editor';
import { reactCustomElementWrapper } from '../../../../../lib/react-custom-element-wrapper';
const EAccordion = reactCustomElementWrapper('e-accordion');
const EAccordionItem = reactCustomElementWrapper('e-accordion-item');
const EAccordionItemLabel = reactCustomElementWrapper('e-accordion-item-label');
const EAccordionItemContent = reactCustomElementWrapper('e-accordion-item-content');
const ESelect = reactCustomElementWrapper('e-select');
const ESwitch = reactCustomElementWrapper('e-switch');

export type VariableListActions = {
  onInputChange: (id: string, value: string) => void;
  onResetClick: (id: string) => void;
  onSearch: (search: string) => void;
  onTypeFilterChange: (value: string) => void;
  onCategoryFilterChange: (value: string) => void;
  onShowVariableIDStringChange: (value: boolean) => void;
};

export type VariableListProps = {
  translations: Translations;
  editableVariables: EditableVariable[];
  useEditableImage: boolean;
  search: string;
  typeFilter: string;
  categoryFilter: string;
  useSearch?: boolean;
  useTypeFilter?: boolean;
  useCategoryFilter?: boolean;
  useCategories?: boolean;
  showVariableIDString?: boolean;
};

export type HeadingsAndVariables = {
  category: string;
  variables: EditableVariable[];
};

type VariableFiltersProps = {
  translations: Translations;
  typeFilter: string;
  categoryFilter: string;
  useTypeFilter?: boolean;
  useCategoryFilter?: boolean;
  showVariableIDString?: boolean;
  categories: string[];
  onTypeFilterChange: (value: string) => void;
  onCategoryFilterChange: (value: string) => void;
  onShowVariableIDStringChange: (value: boolean) => void;
};

const VariableFilters = (props: VariableFiltersProps) => {
  if (!props.useTypeFilter && !props.useCategoryFilter) {
    return null;
  }
  return (
    <div className="e-margin-bottom-s" style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
      {props.useTypeFilter && (
        <ESelect>
          <select
            className="e-select"
            value={props.typeFilter}
            onChange={e => props.onTypeFilterChange((e.target as HTMLSelectElement).value)}
          >
            <option value="">{props.translations.typeFilterPlaceholder}</option>
            {Object.keys(props.translations.types).map(t => (
              <option key={t} value={t}>
                {props.translations.types[t as keyof typeof props.translations.types]}
              </option>
            ))}
          </select>
        </ESelect>
      )}
      {props.useCategoryFilter && (
        <ESelect>
          <select
            className="e-select"
            value={props.categoryFilter}
            onChange={e => props.onCategoryFilterChange((e.target as HTMLSelectElement).value)}
          >
            <option value="">{props.translations.categoryFilterPlaceholder}</option>
            {props.categories.map(cat => (
              <option key={cat} value={cat}>
                {props.translations.categories[cat as keyof typeof props.translations.categories] || cat}
              </option>
            ))}
          </select>
        </ESelect>
      )}
      <ESwitch
        label={props.translations.showVariableIDStringLabel}
        checked={props.showVariableIDString || undefined}
        on-change={(e: Event) => props.onShowVariableIDStringChange((e.target as HTMLInputElement).checked)}
      />
    </div>
  );
};

const variablesGroupedByCategory = (
  editableVariables: EditableVariable[],
  search: string,
  typeFilter: string,
  categoryFilter: string,
): HeadingsAndVariables[] => {
  const grouped = editableVariables.reduce(
    (acc, variable) => {
      if (search && !variable.name.toLowerCase().includes(search.toLowerCase())) {
        return acc;
      }
      if (typeFilter && variable.type !== typeFilter) {
        return acc;
      }
      const category = variable.category || 'uncategorized';
      if (categoryFilter && category !== categoryFilter) {
        return acc;
      }
      if (!acc[category]) {
        acc[category] = [];
      }
      acc[category].push(variable);
      return acc;
    },
    {} as Record<string, EditableVariable[]>,
  );

  return Object.entries(grouped)
    .map(([category, variables]) => ({
      category,
      variables: variables.sort((a, b) => a.name.localeCompare(b.name)),
    }))
    .sort((a, b) => a.category.localeCompare(b.category));
};

export const VariableList = (props: VariableListProps & VariableListActions) => {
  const availableCategories = [...new Set(props.editableVariables.map(v => v.category || 'uncategorized'))].sort(
    (a, b) => a.localeCompare(b),
  );

  const filters = (
    <VariableFilters
      translations={props.translations}
      typeFilter={props.typeFilter}
      categoryFilter={props.categoryFilter}
      useTypeFilter={props.useTypeFilter}
      useCategoryFilter={props.useCategoryFilter}
      showVariableIDString={props.showVariableIDString}
      categories={availableCategories}
      onTypeFilterChange={props.onTypeFilterChange}
      onCategoryFilterChange={props.onCategoryFilterChange}
      onShowVariableIDStringChange={props.onShowVariableIDStringChange}
    />
  );

  if (props.useCategories) {
    return (
      <div>
        {props.useSearch && (
          <div className="e-margin-bottom-s">
            <input
              id="variable-search-input"
              className="e-input e-input-search"
              placeholder={props.translations.searchInputPlaceholder}
              type="search"
              onInput={e => props.onSearch(e.currentTarget.value)}
            />
          </div>
        )}
        {filters}
        <EAccordion>
          {variablesGroupedByCategory(
            props.editableVariables,
            props.search,
            props.typeFilter,
            props.categoryFilter,
          ).map(group => (
            <EAccordionItem active>
              <EAccordionItemLabel>
                <span className="text-size-title">
                  {props.translations.categories[group.category as keyof typeof props.translations.categories] ||
                    group.category}
                </span>
              </EAccordionItemLabel>
              <EAccordionItemContent>
                {group.variables.map(editableVariable => {
                  return (
                    <VariableEditor
                      key={editableVariable.id}
                      editableVariable={editableVariable}
                      translations={props.translations}
                      onResetClick={props.onResetClick}
                      onInputChange={props.onInputChange}
                      useEditableImage={props.useEditableImage}
                      showVariableIDString={props.showVariableIDString}
                    />
                  );
                })}
              </EAccordionItemContent>
            </EAccordionItem>
          ))}
        </EAccordion>
      </div>
    );
  }
  return (
    <div>
      {props.useSearch && (
        <div className="e-margin-bottom-s">
          <input
            id="variable-search-input"
            className="e-input e-input-search"
            placeholder={props.translations.searchInputPlaceholder}
            type="search"
            onInput={e => props.onSearch(e.currentTarget.value)}
          />
        </div>
      )}
      {filters}
      {props.editableVariables
        .filter(v => !props.search || v.name.toLowerCase().includes(props.search.toLowerCase()))
        .filter(v => !props.typeFilter || v.type === props.typeFilter)
        .filter(v => !props.categoryFilter || (v.category || 'uncategorized') === props.categoryFilter)
        .map(editableVariable => (
          <VariableEditor
            key={editableVariable.id}
            editableVariable={editableVariable}
            translations={props.translations}
            onResetClick={props.onResetClick}
            onInputChange={props.onInputChange}
            useEditableImage={props.useEditableImage}
            showVariableIDString={props.showVariableIDString}
          />
        ))}
    </div>
  );
};
